import React, { useState, useEffect, useCallback } from "react";
import { BarChart3 } from "lucide-react";
import DateLabelInput from "../common/forms/DateLabelInput";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { MdLockReset } from "react-icons/md";
import { useAuth } from "../../context/AuthContext"; // Auth context

export default function Totalquantityanddutyinbondexbondreport() {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
    });
    const { logout } = useAuth();

    // Handle date change
    const handleDateChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({ startDate: "", endDate: "" });
        fetchReport();
    };

    // Fetch function
    const fetchReport = useCallback(async (start = "", end = "") => {
        setLoading(true);

        let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.GET_TOTAL_QUANTITY_INBIND_EXBOND}`;

        if (start && end) {
            url += `?start_date=${start}&end_date=${end}`;
        }

        try {
            let response = await fetch(url, { method: "GET", headers: getHeaders(true) });

            // -------------------------
            // 🔥 Handle 401 token expired
            // -------------------------
            if (response.status === 401) {
                console.warn("Access token expired. Attempting refresh...");
                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry the same API call with new token
                    response = await fetch(url, { method: "GET", headers: getHeaders(true) });
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout(); // redirect user
                    return;
                }
            }



            const result = await response.json();
            if (result.status === 200) {
                setReportData(result.data);
            }
        } finally {
            setLoading(false);
        }
    }, [logout]);


    // Load FULL DATA on first page load
    useEffect(() => {
        fetchReport();
    }, [fetchReport]);


    // Load FILTERED DATA automatically when both dates selected
    useEffect(() => {
        if (filters.startDate && filters.endDate) {
            fetchReport(filters.startDate, filters.endDate);
        }
    }, [filters.startDate, filters.endDate, fetchReport]);


    const formatIndianNumber = (num) => {
        if (num === null || num === undefined || isNaN(num)) return "0";
        return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };


    return (
        <div className="w-full xl:w-1/2 bg-white p-6 rounded border border-gray-100 shadow-sm hover:shadow-md transition-shadow">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-dashed pb-3 mb-5">
                <div className="flex items-center gap-2">
                    <BarChart3 className="text-indigo-500" size={20} />
                    <h2 className="text-base font-semibold text-gray-900">
                        Total Quantity & Duty (Inbond & Exbond)
                    </h2>
                </div>
            </div>
            {/* Date Filters */}
            <div className="flex items-end gap-4 mb-6 flex-wrap xl:flex-nowrap">

                <div className="w-full sm:w-auto">
                    <DateLabelInput
                        label="Start Date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={(e) => handleDateChange("startDate", e.target.value)}
                    />
                </div>

                <div className="w-full sm:w-auto">
                    <DateLabelInput
                        label="End Date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={(e) => handleDateChange("endDate", e.target.value)}
                    />
                </div>

                {/* Reset Button (Styled) */}
                {(filters.startDate || filters.endDate) && (
                    <button
                        onClick={resetFilters}
                        className="flex items-center justify-center h-11 w-11"
                        title="Reset Filters"
                    >
                        <MdLockReset size={20} />
                    </button>
                )}
            </div>


            {loading && <div className="text-center py-3">Loading...</div>}

            {!loading && !reportData && (
                <div className="text-gray-500 text-center">No data available</div>
            )}

            {/* Table */}
            {reportData && (
                <div className="overflow-hidden">
                    <div className="flex text-xs font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                        <div className="px-4 py-2.5 w-1/2">Type</div>
                        <div className="px-4 py-2.5 w-1/4 text-right">Total Quantity (KG)</div>
                        <div className="px-4 py-2.5 w-1/4 text-right">Total Duty (₹)</div>
                    </div>

                    <div className="divide-y divide-gray-100 text-table text-gray-800">
                        {[
                            [
                                "Inbond",
                                formatIndianNumber(reportData.inbond_data.total_weight_inbond),
                                formatIndianNumber(reportData.inbond_data.total_duty_inbond),
                            ],
                            [
                                "Exbond",
                                formatIndianNumber(reportData.exbond_data.total_weight_exbond),
                                formatIndianNumber(reportData.exbond_data.total_duty_exbond),
                            ],
                            [
                                "Difference (Inbond - Exbond)",
                                formatIndianNumber(reportData.difference.difference_weight),
                                formatIndianNumber(reportData.difference.difference_duty),
                            ],
                        ].map((row, i) => (
                            <div key={i} className={`flex ${i === 2 ? "bg-gray-50 font-semibold" : ""}`}>
                                <div className="w-1/2 px-4 py-2.5">{row[0]}</div>
                                <div className="w-1/4 px-4 py-2.5 text-right">{row[1]}</div>
                                <div className="w-1/4 px-4 py-2.5 text-right">{row[2]}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

